import React, { useState } from 'react';
import { utils } from '@ac/client';
import { Spinner } from './Spinner';

interface ExportModalProps {
  onClose: () => void;
  onExport: () => Promise<void>;
}

export function ExportModal({ onClose, onExport }: ExportModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport();
      setExportComplete(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-base-900/50 flex items-center justify-center p-4 z-50">
      <div className="shadow-xl max-w-md w-full p-6 animate-fade-in bg-base-800 rounded-xl">
        <h2 className="text-xl font-semibold mb-4 text-base-100">Export Your Data</h2>
        
        {!exportComplete ? (
          <>
            <p className="mb-6 text-base-200">
              Export all your conversations, saved facts, and memories. Your data will be downloaded
              as a JSON file that you can keep for your records.
            </p>
            
            <div className="p-4 mb-6 bg-base-900/50 rounded-lg">
              <h3 className="font-medium mb-2 text-base-100">What's included:</h3>
              <ul className="text-sm space-y-1 text-base-400">
                <li>• All conversations and messages</li>
                <li>• Saved facts and memories</li>
                <li>• Usage statistics</li>
                <li>• Archived data (if any)</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className={`flex-1 px-4 py-2 transition font-medium rounded-lg disabled:cursor-not-allowed ${
                  isExporting 
                    ? 'bg-base-700 text-base-400' 
                    : 'bg-primary-500 hover:bg-primary-600 text-white'
                }`}
              >
                {isExporting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner />
                    Exporting...
                  </span>
                ) : (
                  'Export Data'
                )}
              </button>
              
              <button
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 transition bg-base-700 hover:bg-base-600 text-base-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-lg font-medium mb-2 text-base-100">Export Complete!</h3>
            <p className="text-base-400">Your data has been downloaded successfully.</p>
          </div>
        )}
      </div>
    </div>
  );
}